import { useState } from "react";
import { withClientContext } from "~/apollo/client";
import { useGetDdqProdOpsWorkflowLogQuery } from "~/graphql/supergraphHooks";
import { formatLogMessage } from "./formatLogMessage";
import type { ActivityFeedEntry } from "../../shared/components/ActivityFeed";

const PAGE_SIZE = 20;

export type UseDdqProdOpsWorkflowLogResult = {
  entries: ActivityFeedEntry[];
  isLoading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  refetch: () => void;
};

// Same fetchMore + updateQuery append pattern as useDdqProdOpsQueueRow.
export const useDdqProdOpsWorkflowLog = (
  workflowId: string,
): UseDdqProdOpsWorkflowLogResult => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { data, loading, fetchMore, refetch } = useGetDdqProdOpsWorkflowLogQuery({
    context: withClientContext("federation"),
    variables: { workflowId, limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: "cache-and-network",
  });

  const page = data?.ddqProdOpsWorkflowLog;
  const rawEntries = page?.entries ?? [];
  const totalCount = page?.totalCount ?? 0;
  const hasMore = rawEntries.length < totalCount;

  const entries: ActivityFeedEntry[] = rawEntries.map((entry) => ({
    id: entry.id,
    timestamp: entry.eventAt,
    actorName: entry.actor?.fullName ?? "System",
    message: formatLogMessage(entry),
  }));

  const onLoadMore = () => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    void fetchMore({
      variables: { offset: rawEntries.length },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous;
        return {
          ddqProdOpsWorkflowLog: {
            ...fetchMoreResult.ddqProdOpsWorkflowLog,
            entries: [
              ...previous.ddqProdOpsWorkflowLog.entries,
              ...fetchMoreResult.ddqProdOpsWorkflowLog.entries,
            ],
          },
        };
      },
    }).finally(() => setIsLoadingMore(false));
  };

  return {
    entries,
    isLoading: loading,
    hasMore,
    isLoadingMore,
    onLoadMore,
    refetch: () => void refetch(),
  };
};
